import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
  AlertTitle,
  TextField,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Code,
  ContentCopy,
  VpnKey,
  Security,
  Api,
  Send,
  QrCode,
  AccountBalance,
  Webhook,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Info,
  Warning,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { authApi } from '@/services/api';
import apiService from '@/services/api';

// =====================================================
// DADOS DOS ENDPOINTS
// =====================================================

interface EndpointInfo {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
}

interface EndpointCategory {
  title: string;
  icon: React.ReactElement;
  endpoints: EndpointInfo[];
}

const endpointCategories: EndpointCategory[] = [
  {
    title: 'QR Code PIX (Simplificado)',
    icon: <QrCode fontSize="small" />,
    endpoints: [
      { method: 'POST', path: '/pix/qrcode', description: 'Gerar QR Code PIX (valor + descricao). Retorna txid, copia-e-cola e imagem base64' },
      { method: 'GET', path: '/pix/qrcode/:txid', description: 'Consultar se o QR Code foi pago (retorna status, dados do pagamento)' },
    ],
  },
  {
    title: 'Enviar PIX',
    icon: <Send fontSize="small" />,
    endpoints: [
      { method: 'POST', path: '/pix/enviar/decode-payload', description: 'Decodificar chave PIX, QR Code ou BR Code' },
      { method: 'POST', path: '/pix/enviar/iniciar', description: 'Iniciar pagamento PIX (etapa 1)' },
      { method: 'POST', path: '/pix/enviar/confirmar', description: 'Confirmar pagamento PIX (etapa 2)' },
      { method: 'GET', path: '/pix/enviar/:endToEndId', description: 'Consultar status de um envio' },
    ],
  },
  {
    title: 'Cobranças Imediatas (Avancado)',
    icon: <QrCode fontSize="small" />,
    endpoints: [
      { method: 'POST', path: '/pix/charges', description: 'Criar cobrança imediata (controle total dos campos)' },
      { method: 'GET', path: '/pix/charges', description: 'Listar cobranças' },
      { method: 'GET', path: '/pix/charges/:txid', description: 'Consultar cobrança por txid' },
      { method: 'GET', path: '/pix/charges/:txid/qrcode', description: 'Obter QR Code da cobrança' },
      { method: 'PUT', path: '/pix/charges/:txid', description: 'Atualizar cobrança' },
      { method: 'DELETE', path: '/pix/charges/:txid', description: 'Cancelar cobrança' },
    ],
  },
  {
    title: 'Cobranças com Vencimento',
    icon: <AccountBalance fontSize="small" />,
    endpoints: [
      { method: 'POST', path: '/pix/cobv', description: 'Criar cobrança com vencimento' },
      { method: 'GET', path: '/pix/cobv', description: 'Listar cobranças com vencimento' },
      { method: 'GET', path: '/pix/cobv/:txid', description: 'Consultar cobrança com vencimento' },
      { method: 'GET', path: '/pix/cobv/:txid/qrcode', description: 'Obter QR Code' },
      { method: 'PUT', path: '/pix/cobv/:txid', description: 'Atualizar cobrança com vencimento' },
      { method: 'DELETE', path: '/pix/cobv/:txid', description: 'Cancelar cobrança com vencimento' },
    ],
  },
  {
    title: 'Pagamentos Recebidos',
    icon: <CheckCircle fontSize="small" />,
    endpoints: [
      { method: 'GET', path: '/pix/payments', description: 'Listar pagamentos recebidos' },
      { method: 'GET', path: '/pix/payments/:e2eid', description: 'Consultar pagamento por endToEndId' },
      { method: 'POST', path: '/pix/payments/:e2eid/refund', description: 'Solicitar devolução/estorno' },
      { method: 'GET', path: '/pix/payments/:e2eid/refund/:refundId', description: 'Consultar devolução' },
    ],
  },
  {
    title: 'Chaves PIX',
    icon: <VpnKey fontSize="small" />,
    endpoints: [
      { method: 'GET', path: '/pix/keys', description: 'Listar chaves PIX' },
      { method: 'GET', path: '/pix/keys/:id', description: 'Consultar chave PIX' },
      { method: 'POST', path: '/pix/keys', description: 'Criar/registrar chave PIX' },
      { method: 'DELETE', path: '/pix/keys/:id', description: 'Excluir chave PIX' },
    ],
  },
  {
    title: 'Webhooks PIX',
    icon: <Webhook fontSize="small" />,
    endpoints: [
      { method: 'GET', path: '/pix/webhooks', description: 'Listar webhooks configurados' },
      { method: 'GET', path: '/pix/webhooks/:chave', description: 'Consultar webhook por chave' },
      { method: 'PUT', path: '/pix/webhooks/:chave', description: 'Registrar/atualizar webhook' },
      { method: 'DELETE', path: '/pix/webhooks/:chave', description: 'Excluir webhook' },
    ],
  },
  {
    title: 'Conta Corrente',
    icon: <AccountBalance fontSize="small" />,
    endpoints: [
      { method: 'GET', path: '/conta-corrente/saldo', description: 'Consultar saldo da conta' },
      { method: 'GET', path: '/conta-corrente/extrato', description: 'Consultar extrato' },
    ],
  },
];

const methodColors: Record<string, string> = {
  GET: '#2e7d32',
  POST: '#1565c0',
  PUT: '#e65100',
  PATCH: '#6a1b9a',
  DELETE: '#c62828',
};

// =====================================================
// COMPONENTE
// =====================================================

const ApiDocsPage: React.FC = () => {
  const theme = useTheme();

  // State
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [expiresIn, setExpiresIn] = useState('30d');
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [showExamples, setShowExamples] = useState(false);

  // Carregar empresas do usuario
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const response = await apiService.get('/user/companies');
        if (response.success && response.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          setCompanies(list);
          if (list.length > 0) {
            setSelectedCompany(list[0].id);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar empresas:', err);
      }
    };
    loadCompanies();
  }, []);

  // Copiar para clipboard
  const copyToClipboard = (text: string, label = 'Texto') => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  // Gerar token
  const handleGenerateToken = async () => {
    if (!selectedCompany) {
      toast.error('Selecione uma empresa');
      return;
    }

    setGenerating(true);
    setGeneratedToken(null);
    setTokenInfo(null);

    try {
      const response = await authApi.generateAgentToken({ companyId: selectedCompany, expiresIn });

      if (response.success && response.data) {
        setGeneratedToken(response.data.token);
        setTokenInfo(response.data);
        toast.success('Token gerado com sucesso!');
      } else {
        toast.error((response as any).error?.message || 'Erro ao gerar token');
      }
    } catch (err: any) {
      toast.error(err?.error?.message || err?.message || 'Erro ao gerar token');
    } finally {
      setGenerating(false);
    }
  };

  // Toggle categoria
  const toggleCategory = (title: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // URL base
  const baseUrl = 'https://api.pay.viptecnologia.com.br/api/v1';

  // Exemplos cURL
  const curlExamples = [
    {
      title: 'Gerar QR Code PIX (simplificado)',
      code: `curl -X POST ${baseUrl}/pix/qrcode \\
  -H "Authorization: Bearer <SEU_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "valor": "25.90",
    "descricao": "Pedido #1234"
  }'

# Retorna:
# - txid: identificador para consultar pagamento
# - pixCopiaECola: texto para colar no app do banco
# - qrCodeBase64: imagem do QR Code em base64 (data:image/png;base64,...)`,
    },
    {
      title: 'Consultar se QR Code foi pago',
      code: `curl -X GET ${baseUrl}/pix/qrcode/<TXID> \\
  -H "Authorization: Bearer <SEU_TOKEN>"

# Retorna:
# - pago: true/false
# - status: ATIVA, CONCLUIDA, REMOVIDA_PELO_USUARIO_RECEBEDOR
# - pagamento: { endToEndId, valor, horario } (quando pago)`,
    },
    {
      title: 'Enviar PIX (Etapa 1 - Iniciar)',
      code: `curl -X POST ${baseUrl}/pix/enviar/iniciar \\
  -H "Authorization: Bearer <SEU_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "valor": "10.00",
    "pixDest": "+5537991265314",
    "infoPagador": "Pagamento via agente"
  }'`,
    },
    {
      title: 'Enviar PIX (Etapa 2 - Confirmar)',
      code: `curl -X POST ${baseUrl}/pix/enviar/confirmar \\
  -H "Authorization: Bearer <SEU_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "endToEndId": "E00360305202602201234abcdef12345"
  }'`,
    },
    {
      title: 'Consultar Saldo',
      code: `curl -X GET ${baseUrl}/conta-corrente/saldo \\
  -H "Authorization: Bearer <SEU_TOKEN>"`,
    },
    {
      title: 'Decodificar Chave PIX / QR Code',
      code: `curl -X POST ${baseUrl}/pix/enviar/decode-payload \\
  -H "Authorization: Bearer <SEU_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "payload": "+5537991265314"
  }'`,
    },
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Api sx={{ fontSize: 32, color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            API & Tokens
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Documentacao da API e gerador de tokens para agentes e automacoes
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* URL Base & Autenticacao */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Code fontSize="small" />
              URL Base & Autenticacao
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                URL Base da API:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 1,
                  p: 1.5,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                }}
              >
                <Box sx={{ flex: 1, overflow: 'auto' }}>{baseUrl}</Box>
                <Tooltip title="Copiar URL">
                  <IconButton size="small" onClick={() => copyToClipboard(baseUrl, 'URL')}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Header de autenticacao:
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  borderRadius: 1,
                  p: 1.5,
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                }}
              >
                <Box sx={{ flex: 1 }}>Authorization: Bearer &lt;SEU_TOKEN&gt;</Box>
                <Tooltip title="Copiar">
                  <IconButton size="small" onClick={() => copyToClipboard('Authorization: Bearer ', 'Header')}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Gerador de Token */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VpnKey fontSize="small" />
              Gerar Token JWT para Agente
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Tokens para Agentes</AlertTitle>
              Gere tokens JWT de longa duracao para uso em agentes de I.A., automacoes e integracoes.
              O token dispensa login e 2FA, permitindo acesso direto a API.
            </Alert>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    label="Empresa"
                  >
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name} ({c.document})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Validade</InputLabel>
                  <Select
                    value={expiresIn}
                    onChange={(e) => setExpiresIn(e.target.value)}
                    label="Validade"
                  >
                    <MenuItem value="7d">7 dias</MenuItem>
                    <MenuItem value="30d">30 dias</MenuItem>
                    <MenuItem value="90d">90 dias</MenuItem>
                    <MenuItem value="365d">1 ano</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGenerateToken}
                  disabled={generating || !selectedCompany}
                  startIcon={generating ? <CircularProgress size={18} /> : <VpnKey />}
                >
                  {generating ? 'Gerando...' : 'Gerar Token'}
                </Button>
              </Grid>
            </Grid>

            {/* Token Gerado */}
            {generatedToken && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Guarde este token com seguranca!</AlertTitle>
                  Este token nao sera mostrado novamente. Copie e guarde em local seguro.
                </Alert>

                {tokenInfo?.company && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip label={`Empresa: ${tokenInfo.company.name}`} size="small" color="primary" variant="outlined" />
                    <Chip label={`Validade: ${tokenInfo.expiresIn}`} size="small" color="info" variant="outlined" />
                    <Chip
                      label={`Expira em: ${new Date(tokenInfo.expiresAt).toLocaleDateString('pt-BR')}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: '#1e1e1e',
                    borderRadius: 1,
                    p: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: '#d4d4d4',
                      wordBreak: 'break-all',
                      pr: 5,
                      maxHeight: 120,
                      overflow: 'auto',
                    }}
                  >
                    {generatedToken}
                  </Box>
                  <Tooltip title="Copiar Token">
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(generatedToken, 'Token')}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#d4d4d4',
                        '&:hover': { bgcolor: alpha('#fff', 0.1) },
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Documentacao dos Endpoints */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Api fontSize="small" />
              Endpoints da API PIX
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Todos os endpoints requerem autenticacao via token JWT no header Authorization.
            </Typography>

            {endpointCategories.map((category) => {
              const isExpanded = expandedCategories[category.title] !== false; // default expanded
              return (
                <Box key={category.title} sx={{ mb: 1 }}>
                  <Box
                    onClick={() => toggleCategory(category.title)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 1.5,
                      px: 2,
                      cursor: 'pointer',
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    {category.icon}
                    <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                      {category.title}
                    </Typography>
                    <Chip label={`${category.endpoints.length}`} size="small" variant="outlined" />
                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </Box>

                  <Collapse in={isExpanded}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell width={90}>Metodo</TableCell>
                            <TableCell>Endpoint</TableCell>
                            <TableCell>Descricao</TableCell>
                            <TableCell width={40}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {category.endpoints.map((ep, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Chip
                                  label={ep.method}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    fontFamily: 'monospace',
                                    bgcolor: alpha(methodColors[ep.method] || '#666', 0.1),
                                    color: methodColors[ep.method] || '#666',
                                    borderColor: alpha(methodColors[ep.method] || '#666', 0.3),
                                  }}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 500 }}
                                >
                                  {ep.path}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {ep.description}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Tooltip title="Copiar URL completa">
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(`${baseUrl}${ep.path}`, 'URL')}
                                  >
                                    <ContentCopy fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              );
            })}
          </Paper>
        </Grid>

        {/* Exemplos de Codigo */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box
              onClick={() => setShowExamples(!showExamples)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
              }}
            >
              <Code fontSize="small" />
              <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                Exemplos de Uso (cURL)
              </Typography>
              {showExamples ? <ExpandLess /> : <ExpandMore />}
            </Box>

            <Collapse in={showExamples}>
              <Box sx={{ mt: 2 }}>
                {curlExamples.map((example, idx) => (
                  <Box key={idx} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {example.title}
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        bgcolor: '#1e1e1e',
                        borderRadius: 1,
                        p: 2,
                        overflow: 'auto',
                      }}
                    >
                      <Box
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: '#d4d4d4',
                          m: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          pr: 4,
                        }}
                      >
                        {example.code}
                      </Box>
                      <Tooltip title="Copiar">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(example.code, 'Exemplo')}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: '#d4d4d4',
                            '&:hover': { bgcolor: alpha('#fff', 0.1) },
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        </Grid>

        {/* Notas */}
        <Grid item xs={12}>
          <Alert severity="info" icon={<Info />}>
            <AlertTitle>Notas Importantes</AlertTitle>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li><strong>QR Code simplificado</strong>: use <code>POST /pix/qrcode</code> para gerar e <code>GET /pix/qrcode/:txid</code> para consultar pagamento.</li>
                <li>O envio de PIX requer 2 etapas: <strong>iniciar</strong> e <strong>confirmar</strong> (com o endToEndId retornado).</li>
                <li>Aguarde pelo menos 3 segundos entre as etapas para evitar rate limit.</li>
                <li>O token JWT inclui o <strong>companyId</strong> - todas as operacoes serao feitas no contexto dessa empresa.</li>
                <li>Formato de valores monetarios: string com 2 casas decimais (ex: "10.50").</li>
                <li>O QR Code gerado tem validade de 24 horas.</li>
              </ul>
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiDocsPage;
